import React, { createContext, useState } from 'react';
import { FETCHING_PAYMENT_STATUS_FAILED_MSG, FETCHING_WEBSHOP_FAILED_MSG, FETCHING_PRODUCT_FAILED_MSG, FETCHING_CART_FAILED_MSG, PRODUCT_OUT_OF_STOCK_ERROR_MSG, REMOVING_PRODUCT_FAILED_MSG, CHECKOUT_SESSION_FAILED_MSG } from '@configs/constants';
const WebshopStateContext = createContext();

export function WebshopProvider({ children }) {
  const [webShop, setWebshop] = useState({});
  const [cart, setCart] = useState({});
  const [selectedPayment, setSelectedPayment] = useState({});
  const [isProductAddedToCart, setIsProductAddedToCart] = useState(false);
  const [stripeEmail, setStripeEmail] = useState('');
  const [swishPhone, setSwishPhone] = useState('');
  const [swishEmail, setSwishEmail] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isProductRemovedFromCart, setIsProductRemovedFromCart] = useState(false);
  const [checkoutSessionResponse, setCheckoutSessionResponse] = useState({});
  const [paymentStatus, setPaymentStatus] = useState('');
  const [isWebShopChecked, setIsWebShopChecked] = useState(false);
  const [product, setProduct] = useState({});
  const [selectedProductForCart, setSelectedProductForCart] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  const fetchPaymentStatus = async (id) => {
    setPaymentStatus('');
    const result = await fetch(`/webshop/api/fetch-payment-status`, {
      method: 'POST',
      body: JSON.stringify({
        checkoutSessionID: id,
      }),
    }).then((resp) => {
      if (resp.status && resp.status !== 200) {
        setErrorMessage(FETCHING_PAYMENT_STATUS_FAILED_MSG);
      } else {
        setPaymentStatus(resp.statusText);
        setErrorMessage('');
      }
      return resp.json();
    });

    if (result && result.paymentStatus) {
      setPaymentStatus(result.paymentStatus);
    }
  };

  const fetchWebshop = async (id) => {
    const result = await fetch(`/webshop/api/fetch-webshop`, {
      method: 'POST',
      body: JSON.stringify({
        webShopToken: id,
      }),
    }).then((resp) => {
      if (resp.status && resp.status !== 200) {
        setErrorMessage(FETCHING_WEBSHOP_FAILED_MSG);
      }
      return resp.json();
    });
    setWebshop(result[0]);
    setIsWebShopChecked(true);
  };

  const fetchProduct = async (id) => {
    setProduct({});
    const result = await fetch(`/webshop/api/fetch-product`, {
      method: 'POST',
      body: JSON.stringify({
        productId: id,
      }),
    }).then((resp) => {
      if (resp.status && resp.status !== 200) {
        setErrorMessage(FETCHING_PRODUCT_FAILED_MSG);
      }
      return resp.json();
    });
    setProduct(result);
  };

  const fetchUserCart = async ({ webShopToken, userId }) => {
    const result = await fetch(`/webshop/api/fetch-cart`, {
      method: 'POST',
      body: JSON.stringify({
        webShopToken,
        userId,
      }),
    }).then((resp) => {
      if (resp.status && resp.status !== 200) {
        setErrorMessage(FETCHING_CART_FAILED_MSG);
      }
      return resp.json();
    });
    setCart(result);
  };

  const addProductToCart = async ({ webShopToken, webShopName, product, userId }) => {
    setIsProductAddedToCart(false);
    await fetch('/webshop/api/add-product-to-cart', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        product,
        webShopToken,
        webShopName,
      }),
    }).then((resp) => {
      if (resp.status && resp.status === 200) {
        setIsProductAddedToCart(true);
      } else {
        setErrorMessage(PRODUCT_OUT_OF_STOCK_ERROR_MSG);
      }
      return resp.json();
    });
  };

  const removeProductFromCart = async ({ userId, productId }) => {
    setIsProductRemovedFromCart(false);
    await fetch('/webshop/api/remove-product-from-cart', {
      method: 'POST',
      body: JSON.stringify({
        userId,
        productId,
      }),
    }).then((resp) => {
      if (resp.status && resp.status === 200) {
        setIsProductRemovedFromCart(true);
      } else {
        setErrorMessage(REMOVING_PRODUCT_FAILED_MSG);
      }
      return resp.json();
    });
  };

  const checkoutSession = async ({ userId, webShopToken, orderDate, email, phone, passId, paymentProviderAccountId }) => {
    const bodyObj = {
      userId,
      webShopToken,
      orderDate,
      email,
      phone,
      passId,
      paymentProviderAccountId,
    };
    setCheckoutSessionResponse({});
    const result = await fetch('/webshop/api/checkout-session', {
      method: 'POST',
      body: JSON.stringify(bodyObj),
    })
      .then((resp) => {
        if (resp.status && resp.status !== 200) {
          setErrorMessage(CHECKOUT_SESSION_FAILED_MSG);
        }
        return resp.json();
      })
      .catch(() => {
        setErrorMessage(CHECKOUT_SESSION_FAILED_MSG);
        return { statusCode: 500 };
      });

    setCheckoutSessionResponse(result);
  };

  let sharedState = {
    webShop,
    cart,
    selectedPayment,
    setSelectedPayment,
    fetchWebshop,
    fetchUserCart,
    addProductToCart,
    stripeEmail,
    setStripeEmail,
    swishPhone,
    setSwishPhone,
    swishEmail,
    setSwishEmail,
    rememberMe,
    setRememberMe,
    isProductAddedToCart,
    isProductRemovedFromCart,
    setIsProductRemovedFromCart,
    removeProductFromCart,
    checkoutSessionResponse,
    checkoutSession,
    fetchPaymentStatus,
    paymentStatus,
    setPaymentStatus,
    isWebShopChecked,
    product,
    setProduct,
    fetchProduct,
    selectedProductForCart,
    setSelectedProductForCart,
    setIsProductAddedToCart,
    errorMessage,
    setErrorMessage,
  };

  return <WebshopStateContext.Provider value={sharedState}>{children}</WebshopStateContext.Provider>;
}

export function useWebshopState() {
  const state = React.useContext(WebshopStateContext);

  if (state === undefined) {
    throw new Error('useWebshopState must be used within a AppProvider');
  }

  return state;
}
